/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Stack, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';

import i18n from '@fai/friendli-suite/features/i18n';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';

import { useCancelFinetuning } from './use-cancel-finetuning';

const cancelFinetuningModalId =
  'dedicated-endpoints-project-finetuning-detail-cancel-finetuning-modal';

export const openCancelFinetuningModal = () => {
  modals.openContextModal({
    modal: 'footerless',
    modalId: cancelFinetuningModalId,
    title: i18n.t('dedicated_endpoints.project.cancel_finetuning'),
    innerProps: { modalBody: <CancelFinetuningModalBody /> },
  });
};

function CancelFinetuningModalBody() {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { cancelFinetuning, loading: cancelFinetuningLoading } = useCancelFinetuning({
    onCompleted: () => {
      modals.close(cancelFinetuningModalId);
    },
  });

  return (
    <Stack gap={theme.pf.layout.xs}>
      <Text variant={TextVariants.BodyMd}>
        {t('dedicated_endpoints.project.this_finetuning_will_be_canceled')}
      </Text>

      <FooterlessModalFooter>
        <Button variant="transparent" onClick={() => modals.close(cancelFinetuningModalId)}>
          {t('dedicated_endpoints.project.not_now')}
        </Button>

        <Button theme="danger" loading={cancelFinetuningLoading} onClick={cancelFinetuning}>
          {t('dedicated_endpoints.project.cancel_job')}
        </Button>
      </FooterlessModalFooter>
    </Stack>
  );
}

CancelFinetuningModalBody.displayName = 'CancelFinetuningModalBody';
